/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 11/11/21
 */

import React, { useCallback, useState } from 'react';
import { CellProps, ControlProps } from '@jsonforms/core';
import merge from 'lodash/merge';
import {
    Flex,
    FormControl,
    FormControlProps,
    FormErrorMessage,
    FormLabel,
    FormLabelProps,
    RequiredIndicator,
} from '@chakra-ui/react';
import { useTouched } from '@/components/WorkplanItemForm/TouchedStateProvider';
import { WorkplanItemFormConfig } from '@/components/WorkplanItemForm/WorkplanItemForm';
import FormHelpText from '@/components/WorkplanItemForm/FormHelpText';
import TextHighlighting from '@/components/TextHighlighting/TextHighlighting';

export interface WithInput {
    input: React.ComponentType<CellProps>;
}

export interface WithFormControlBreakoutProps {
    formControlProps?: FormControlProps;
    labelProps?: FormLabelProps;
}

export const ChakraFormInput = (props: ControlProps & WithInput & WithFormControlBreakoutProps) => {
    const {
        id,
        errors,
        label,
        uischema,
        schema,
        visible,
        required,
        config,
        input,
        path,
        formControlProps,
        labelProps,
    } = props;
    const [, setFocused] = useState(false);
    const { isTouched, setTouched } = useTouched(id);
    const newConfig = config as WorkplanItemFormConfig;

    const isSubmitted = newConfig.isSubmitted;
    const hasError = errors.length > 0;
    const doShowError = hasError && (isTouched || isSubmitted);

    const appliedUiSchemaOptions = merge({}, config, uischema.options);

    const InnerComponent = input;
    const onFocus = useCallback(() => setFocused(true), []);
    const onBlur = useCallback(() => {
        setFocused(false);
        setTouched(id);
    }, [setTouched, id]);

    const inputId = `${id}-input`;

    if ('private' in schema && schema['private']) {
        if (!appliedUiSchemaOptions.isAuthor) {
            return null;
        }
    }

    const hasChanges = newConfig.formChanges[path];
    const flexDirection = appliedUiSchemaOptions.flexDirection;
    const minWidth = appliedUiSchemaOptions.minWidth;
    const whiteSpace = appliedUiSchemaOptions.whiteSpace;
    return (
        <FormControl
            id={id}
            isRequired={required}
            {...formControlProps}
            hidden={!visible}
            onFocus={onFocus}
            onBlur={onBlur}
            isInvalid={doShowError}
        >
            {flexDirection === 'row' && (
                <Flex mb={3}>
                    <FormHelpText uischema={uischema} />
                </Flex>
            )}
            <Flex flexDirection={flexDirection || 'column'} gridGap={3}>
                <FormLabel
                    {...labelProps}
                    htmlFor={inputId}
                    requiredIndicator={<RequiredIndicator color="red.600" />}
                    minWidth={minWidth || undefined}
                    whiteSpace={whiteSpace || undefined}
                >
                    <TextHighlighting
                        version={1}
                        query={hasChanges && appliedUiSchemaOptions.format !== 'radio' ? label : ''}
                    >
                        {label}
                    </TextHighlighting>
                </FormLabel>
                {(!flexDirection || flexDirection !== 'row') && <FormHelpText uischema={uischema} />}
                <InnerComponent {...props} id={inputId} isValid={!doShowError} visible={visible} />
            </Flex>

            {doShowError && <FormErrorMessage color="red.600">{errors}</FormErrorMessage>}
        </FormControl>
    );
};
